namespace Solovis.Reports.Parts.Tables {

    export interface DataTableSeriesProcessorOptions {
        tableControl: DataTableControl;
                
        seriesExportList: DataTableSeriesExportConfiguration[];

        argument?: any;
        sortByFirstSeriesValue?: "asc" | "desc";
        firstNDataRows?: number;
    }

    export interface DataTableSeriesExportedData {
        seriesList: any[];
        dataEntries: any[];        
        argumentField: string;

        firstArgumentColumnEncountered: DataTableHelpers.Column;
    }

    export interface DataTableSeriesExportConfiguration {     
        argumentColumnId?: string;        

        columnIdsAsArguments?: string[];
        valueColumnId?: string;
        rangeValue1ColumnId?: string;
        rangeValue2ColumnId?: string;
        sizeColumnId?: string;
        tagColumnId?: string;

        colorColumnId?: string;
        color?: string;

        /**
         * If more than one value is present for a given argument, we will
         * use this method to aggregate the values. The default will be "pickOne".
         * Higher-level code that converts tables to charts might choose to alter
         * the value passed in depending on the chart type. Valid options are "sum" and "pickOne".
         */
        valueAggregation?: string;

        /**
         * If more than one value is present for a given argument, we will
         * use this method to aggregate the values. The default will be "pickOne".
         * Higher-level code that converts tables to charts might choose to alter
         * the value passed in depending on the chart type. Valid options are "sum" and "pickOne".
         */
        sizeAggregation?: string;

        /**
         * If rangeFromValue is set to true, range will be determined from "valueColumnId" values. This will
         * override the rangeValue1ColumnId and rangeValue2ColumnId settings.
         */
        rangeFromValue?: boolean;

        /**
         * If set, the values for the provided column ids should be used as the series definitions.
         */
        seriesByColumnIds?: string[],

        defaultSeriesName?: string,

        /**
         * seriesSettings is a collection of values that will be copied into each created series object.
         */
        seriesSettings?: any,     

        /**
         * type may be one of the supported series types such as "line" or "bar".
         */
        type?: string,

        /**
         * Describes characteristics of points on the series.
         */
        point?: any;

        /**
         * Describes characteristics of labels on the series.
         */
        label?: any;

        /**
         * Width of line for line based charts.
         */
        lineWidth?: any;

        /**
         * If series is stackedbar or fullstackedbar, then stack can group values into separate stacks.
         */
        stack?: string;

        /**
         * If pane is set, this series will be associated with the chart pane of the same name.
         */
        pane?: string;

        /**
         * If axis is set, this series will be associated with the axis of the same name.
         */
        axis?: string;

        /**
         * showInLegend defaults to true
         */
        showInLegend?: boolean;
    }


    /**
     * Active configuration state for a single DataTableSeriesExportConfiguration that is being processed.
     */
    interface DataTableSeriesProcessorExportState {
        argumentColumn?: DataTableHelpers.Column;
        columnsAsArguments?: DataTableHelpers.Column[];
        seriesColumns?: DataTableHelpers.Column[];       
        valueColumn?: DataTableHelpers.Column;
        rangeValue1Column?: DataTableHelpers.Column;
        rangeValue2Column?: DataTableHelpers.Column;
        sizeColumn?: DataTableHelpers.Column;
        tagColumn?: DataTableHelpers.Column;

        colorColumn?: DataTableHelpers.Column;

        config: DataTableSeriesExportConfiguration;

        /**
         * Index of config object within seriesExportList
         */
        seriesExportIndex: number;
    }

    export class DataTableSeriesProcessor {

        private _options: DataTableSeriesProcessorOptions;
        private _tableControl: DataTableControl;                    
        
        private _activeExportState: DataTableSeriesProcessorExportState;

        private _hasExported: boolean;

        private _nestedGroupKeyToRowModelMap: { [index: string]: any };
        private _nestedGroupKeyToHasChildrenMap: { [index: string]: boolean };

        private _seriesList: any[];        

        private _dataEntries: any[];
        private _argumentValueToDataEntryMap: any;

        /**
         * We remember first argument column so that we can try to auto set the format.
         */
        private _firstArgumentColumnEncountered: DataTableHelpers.Column;


        constructor() {            
        }

        public static exportSeriesData(options: DataTableSeriesProcessorOptions): DataTableSeriesExportedData {
            let processor = new DataTableSeriesProcessor();
            let seriesData = processor.exportSeriesData(options);
            return seriesData;
        }

        private setActiveExportConfig(exportConfig: DataTableSeriesExportConfiguration, seriesExportIndex: number) {
            let argumentColumn: DataTableHelpers.Column;
            let columnsAsArguments: DataTableHelpers.Column[];
            let seriesColumns: DataTableHelpers.Column[];
            let valueColumn: DataTableHelpers.Column;
            let rangeValue1Column: DataTableHelpers.Column;
            let rangeValue2Column: DataTableHelpers.Column;
            let sizeColumn: DataTableHelpers.Column;
            let tagColumn: DataTableHelpers.Column;
            let colorColumn: DataTableHelpers.Column;

            if (!_.isEmpty(exportConfig.columnIdsAsArguments)) {
                columnsAsArguments = exportConfig.columnIdsAsArguments.map((colId) => {
                    return this._tableControl.columnSet.getColumnById(colId);
                });

                if (!this._firstArgumentColumnEncountered) {
                    this._firstArgumentColumnEncountered = columnsAsArguments[0];
                }
            }
            else {

                if (exportConfig.argumentColumnId) {
                    argumentColumn = this._tableControl.columnSet.getColumnById(exportConfig.argumentColumnId);
                }                

                if (!this._firstArgumentColumnEncountered) {
                    this._firstArgumentColumnEncountered = argumentColumn;
                }

                if (exportConfig.valueColumnId) {
                    valueColumn = this._tableControl.columnSet.getColumnById(exportConfig.valueColumnId);
                }

                if (exportConfig.rangeValue1ColumnId) {
                    rangeValue1Column = this._tableControl.columnSet.getColumnById(exportConfig.rangeValue1ColumnId);
                }

                if (exportConfig.rangeValue2ColumnId) {
                    rangeValue2Column = this._tableControl.columnSet.getColumnById(exportConfig.rangeValue2ColumnId);
                }

                if (exportConfig.sizeColumnId) {
                    sizeColumn = this._tableControl.columnSet.getColumnById(exportConfig.sizeColumnId);
                }                
            }

            if (exportConfig.tagColumnId) {
                tagColumn = this._tableControl.columnSet.getColumnById(exportConfig.tagColumnId);
            }

            if (exportConfig.seriesByColumnIds) {
                seriesColumns = exportConfig.seriesByColumnIds.map((colId) => {
                    return this._tableControl.columnSet.getColumnById(colId);
                });                
            }                        

            if (exportConfig.colorColumnId) {
                colorColumn = this._tableControl.columnSet.getColumnById(exportConfig.colorColumnId);
            }

            this._activeExportState = {
                argumentColumn: argumentColumn,
                columnsAsArguments: columnsAsArguments,
                seriesColumns: seriesColumns,
                valueColumn: valueColumn,
                rangeValue1Column: rangeValue1Column,
                rangeValue2Column: rangeValue2Column,
                sizeColumn: sizeColumn,
                tagColumn: tagColumn,
                colorColumn: colorColumn,
                config: exportConfig,

                seriesExportIndex: seriesExportIndex
            };
        }

        private prescanFlattenedRows(flattenedRows) {
            this._nestedGroupKeyToRowModelMap = {};
            this._nestedGroupKeyToHasChildrenMap = {};

            for (let rowModel of flattenedRows) {
                if (rowModel.isAggregateRow) {
                    // Skip aggregate rows.
                    continue;
                }

                if (rowModel.isGroupHeader) {
                    this._nestedGroupKeyToRowModelMap[rowModel.nestedGroupKey] = rowModel;                    
                }

                if (rowModel.parentRow && rowModel.parentRow.nestedGroupKey) {
                    this._nestedGroupKeyToHasChildrenMap[rowModel.parentRow.nestedGroupKey] = true;
                }
            }
        }

        private isLeafRowModel(rowModel): boolean {
            if (rowModel.isGroupHeader) {
                if (!this._nestedGroupKeyToHasChildrenMap[rowModel.nestedGroupKey]) {
                    return true;
                }
            }
            else if (rowModel.isAggregateRow) {
                return false;
            }
            else {
                return true;
            }
        }       

        private getSeriesForSeriesName(seriesName: string) {
            let series = null;
            for (let existingSeries of this._seriesList) {
                if (existingSeries.name === seriesName && existingSeries._seriesExportIndex === this._activeExportState.seriesExportIndex) {
                    series = existingSeries;
                    break;
                }
            }
            
            if (!series) {
                series = {};
                if (this._activeExportState.config.seriesSettings) {
                    jQuery.extend(series, this._activeExportState.config.seriesSettings);
                }

                let seriesIndex = this._seriesList.length;

                series.name = seriesName;
                series._seriesExportIndex = this._activeExportState.seriesExportIndex;
                series._valueColumn = this._activeExportState.valueColumn;
                series._columnsAsArguments = this._activeExportState.columnsAsArguments;

                if (this._activeExportState.config.type) {
                    series.type = this._activeExportState.config.type;
                }
                if (this._activeExportState.config.point) {
                    series.point = Solovis.deepCopy(this._activeExportState.config.point);
                }
                if (this._activeExportState.config.label) {
                    series.label = Solovis.deepCopy(this._activeExportState.config.label);
                }
                if (this._activeExportState.config.stack) {
                    series.stack = this._activeExportState.config.stack;
                }
                if (this._activeExportState.config.lineWidth) {
                    series.width = this._activeExportState.config.lineWidth;
                }
                if (this._activeExportState.config.pane) {
                    series.pane = this._activeExportState.config.pane;
                }
                if (this._activeExportState.config.axis) {
                    series.axis = this._activeExportState.config.axis;
                }
                if (Solovis.isNotNull(this._activeExportState.config.showInLegend)) {
                    series.showInLegend = this._activeExportState.config.showInLegend;
                }
                else {
                    series.showInLegend = true;
                }

                if (this._activeExportState.valueColumn || this._activeExportState.columnsAsArguments) {
                    series.valueField = "value_" + seriesIndex;
                }
                if (this._activeExportState.rangeValue1Column || this._activeExportState.config.rangeFromValue) {
                    series.rangeValue1Field = "rValue1_" + seriesIndex;
                }
                if (this._activeExportState.rangeValue2Column || this._activeExportState.config.rangeFromValue) {
                    series.rangeValue2Field = "rValue2_" + seriesIndex;
                }
                if (this._activeExportState.sizeColumn) {
                    series.sizeField = "size_" + seriesIndex;
                }
                if (this._activeExportState.tagColumn) {
                    series.tagField = "tag_" + seriesIndex;
                }

                this._seriesList.push(series);                
            }             

            return series;
        }

        private getDataEntryForArgumentValue(argValue: any) {
            let dataEntry = null;

            dataEntry = this._argumentValueToDataEntryMap[argValue];
            if (!dataEntry) {
                dataEntry = { argValue: argValue };

                this._dataEntries.push(dataEntry);
                this._argumentValueToDataEntryMap[argValue] = dataEntry;
            }

            return dataEntry;
        }

        private exportSeriesData(options: DataTableSeriesProcessorOptions): DataTableSeriesExportedData {
            if (this._hasExported) {
                throw "exportSeriesData has already been called for this instance";
            }

            this._hasExported = true;

            this._options = options;
            this._tableControl = options.tableControl;                        

            let dataView = this._tableControl.dataView;                       

            this.prescanFlattenedRows(dataView.flattenedRows);     

            // Initialize series and data collections.
            this._seriesList = [];            

            this._dataEntries = [];
            this._argumentValueToDataEntryMap = {};

            if (this._options.seriesExportList) {
                let seriesExportCo = 0;
                for (let exportConfig of this._options.seriesExportList) {
                    this.setActiveExportConfig(exportConfig, seriesExportCo++);
                    
                    let rowData = dataView.flattenedRows; 

                    for (let rowModel of rowData) {
                        if (!this.isLeafRowModel(rowModel)) {
                            continue;
                        }                        

                        let seriesName = this.getSeriesNameForRowModel(rowModel);
                        let series = this.getSeriesForSeriesName(seriesName);

                        if (!series.color) {
                            if (this._activeExportState.colorColumn) {
                                // The first row in the series gets to pick the color.                        
                                let color = this.getColumnValueForRowModel(this._activeExportState.colorColumn, rowModel);
                                series.color = color;
                            }                        
                            else if (this._activeExportState.config.color) {
                                series.color = this._activeExportState.config.color;
                            }
                        }

                        if (this._activeExportState.columnsAsArguments) {
                            for (let colAsArgument of this._activeExportState.columnsAsArguments) {
                                if (colAsArgument == null)
                                    continue;

                                let argValue = colAsArgument.getCompositeLabel();
                                let dataEntry = this.getDataEntryForArgumentValue(argValue);

                                //TODO: figure out what columns as arguments means, this might be a good place for stopping drill down if enabled
                                let seriesValue = this.getColumnValueForRowModel(colAsArgument, rowModel);
                                this.addValueToObject(dataEntry, series.valueField, seriesValue);

                                if (this._activeExportState.config.rangeFromValue) {
                                    this.addRangeValue1ToObject(dataEntry, series.rangeValue1Field, seriesValue);
                                    this.addRangeValue2ToObject(dataEntry, series.rangeValue2Field, seriesValue);
                                }

                                if (Solovis.isNotNull(seriesValue)) {
                                    series._hasNonNullValue = true;
                                }
                            }
                        }
                        else {
                            let argValue = this.getColumnValueForRowModel(this._activeExportState.argumentColumn, rowModel);                            
                            let dataEntry = this.getDataEntryForArgumentValue(argValue);
                            dataEntry.groupKey = rowModel.row._groupKey;

                            if (this._activeExportState.valueColumn) {
                                let seriesValue = this.getColumnValueForRowModel(this._activeExportState.valueColumn, rowModel);
                                this.addValueToObject(dataEntry, series.valueField, seriesValue);

                                if (this._activeExportState.config.rangeFromValue) {
                                    this.addRangeValue1ToObject(dataEntry, series.rangeValue1Field, seriesValue);
                                    this.addRangeValue2ToObject(dataEntry, series.rangeValue2Field, seriesValue);
                                }

                                if (Solovis.isNotNull(seriesValue)) {
                                    series._hasNonNullValue = true;
                                }
                            }

                            if (!this._activeExportState.config.rangeFromValue) {
                                if (this._activeExportState.rangeValue1Column) {
                                    let rangeValue1 = this.getColumnValueForRowModel(this._activeExportState.rangeValue1Column, rowModel);
                                    this.addRangeValue1ToObject(dataEntry, series.rangeValue1Field, rangeValue1);

                                    if (Solovis.isNotNull(rangeValue1)) {
                                        series._hasNonNullValue = true;
                                    }
                                }

                                if (this._activeExportState.rangeValue2Column) {
                                    let rangeValue2 = this.getColumnValueForRowModel(this._activeExportState.rangeValue2Column, rowModel);
                                    this.addRangeValue2ToObject(dataEntry, series.rangeValue2Field, rangeValue2);

                                    if (Solovis.isNotNull(rangeValue2)) {
                                        series._hasNonNullValue = true;
                                    }
                                }
                            }

                            if (this._activeExportState.sizeColumn) {
                                let sizeValue = this.getColumnValueForRowModel(this._activeExportState.sizeColumn, rowModel);
                                this.addSizeToObject(dataEntry, series.sizeField, sizeValue);
                            }

                            if (this._activeExportState.tagColumn) {
                                let tagValue = this.getColumnValueForRowModel(this._activeExportState.tagColumn, rowModel);
                                if (Solovis.isNull(dataEntry[series.tagField])) {
                                    dataEntry[series.tagField] = tagValue;
                                }
                            }
                        }
                    }
                }
            }       

            let dataEntriesToUse = this._dataEntries;

            if (this._options.sortByFirstSeriesValue) {
                for (let seriesEntry of this._seriesList) {
                    if (seriesEntry.valueField) {
                        dataEntriesToUse = _.sortBy(dataEntriesToUse, function (e) { return e[seriesEntry.valueField]; });                        
                        if (this._options.sortByFirstSeriesValue === "desc") {
                            dataEntriesToUse.reverse();
                        }
                        break;
                    }
                }
            }

            if (this._options.firstNDataRows) {
                if (dataEntriesToUse.length > this._options.firstNDataRows) {
                    dataEntriesToUse = dataEntriesToUse.slice(0, this._options.firstNDataRows);
                }
            }

            let seriesData: DataTableSeriesExportedData = {
                seriesList: this._seriesList,
                dataEntries: dataEntriesToUse,
                argumentField: "argValue",            
                firstArgumentColumnEncountered: this._firstArgumentColumnEncountered
            };            

            return seriesData;
        }             

        private addValueToObject(dataEntry: any, fieldName: string, value: any) {
            let valueAggMode = this._activeExportState.config.valueAggregation;
            if (!valueAggMode) {
                valueAggMode = "pickOne";
            }
            if (Solovis.isNull(dataEntry[fieldName])) {
                dataEntry[fieldName] = value;
            }
            else {
                if (valueAggMode === "sum") {
                    if (Solovis.isNotNull(value)) {
                        dataEntry[fieldName] = dataEntry[fieldName] + value;
                    }
                }
            }
        }

        private addRangeValue1ToObject(dataEntry: any, fieldName: string, value: any) {
            if (Solovis.isNull(dataEntry[fieldName])) {
                dataEntry[fieldName] = value;
            }
            else {                
                if (Solovis.isNotNull(value)) {
                    dataEntry[fieldName] = Math.min(dataEntry[fieldName], value);
                }                
            }
        }

        private addRangeValue2ToObject(dataEntry: any, fieldName: string, value: any) {
            if (Solovis.isNull(dataEntry[fieldName])) {
                dataEntry[fieldName] = value;
            }
            else {
                if (Solovis.isNotNull(value)) {
                    dataEntry[fieldName] = Math.max(dataEntry[fieldName], value);
                }
            }
        }

        private addSizeToObject(dataEntry: any, fieldName: string, value: any) {
            let sizeAggMode = this._activeExportState.config.sizeAggregation;
            if (!sizeAggMode) {
                sizeAggMode = "pickOne";
            }
            if (Solovis.isNull(dataEntry[fieldName])) {
                dataEntry[fieldName] = value;
            }
            else {
                if (sizeAggMode === "sum") {
                    if (Solovis.isNotNull(value)) {
                        dataEntry[fieldName] = dataEntry[fieldName] + value;
                    }
                }
            }
        }


        private getSeriesNameForRowModel(rowModel) {
            let columns = this._activeExportState.seriesColumns;

            if (_.isEmpty(columns)) {
                if (this._activeExportState.config.defaultSeriesName) {
                    return this._activeExportState.config.defaultSeriesName;
                }
                else {
                    if (this._activeExportState.valueColumn) {
                        return this._activeExportState.valueColumn.getCompositeLabel();
                    }
                    else {
                        return "";
                    }
                }
            }

            let seriesName = "";

            let co = 0;
            for (let col of columns) {
                let val = this.getColumnValueForRowModel(col, rowModel);

                if (co > 0) {
                    seriesName += " : ";
                }

                seriesName += val;

                co++;
            }

            return seriesName;
        }

        /**
         * Wrapper around the method to get the underlying value to transform dayIds into Dates
         * and other special id's into strings.
         * @param col
         * @param rowModel
         */
        private getColumnValueForRowModel(col: DataTableHelpers.Column, rowModel) {
            let cellValue = this.internalGetColumnValueForRowModel(col, rowModel);
            if (Solovis.isNull(cellValue)) {
                return cellValue;
            }

            if (typeof cellValue === "string") {
                return cellValue;
            }

            if (col.dataType === "dayId") {
                let date = InvData.DayId.toDate(cellValue);
                return date;
            }

            if (col.dataType === "entityId") {
                let label = this._tableControl.modelEnvironment.labelHelper.getEntityLabel(cellValue);
                return label;
            }

            if (col.dataType === "investmentId") {
                let label = this._tableControl.modelEnvironment.labelHelper.getInvestmentLabel(cellValue);
                return label;
            }

            if (col.dataType === "currencyId") {
                let label = this._tableControl.modelEnvironment.labelHelper.getCurrencyCodeLabel(cellValue);
                return label;
            }

            if (col.dataType === "tagId") {
                let tagSet = this._tableControl.modelEnvironment.tagSets.tryGetById(col.definitionToUse.tagSetId);
                if (tagSet) {
                    let tag = tagSet.tryGetTagById(cellValue);
                    if (tag) {
                        return tag.label;
                    }
                }                
            }

            return cellValue;
        }

        private internalGetColumnValueForRowModel(col: DataTableHelpers.Column, rowModel) {
            let self = this;

            if (!col || !rowModel) {
                return null;
            }

            if (rowModel.isGroupHeader) {
                let actualRow = rowModel.row;

                var cellValueGetter;
                if (actualRow._backingRow) {
                    cellValueGetter = function (row, col) {
                        var cellValue = self._tableControl._getColumnValueForRow(col, row._backingRow);
                        return cellValue;
                    };
                }
                else {
                    cellValueGetter = function (row, col) {
                        var cellValue = row._groupTotals ? row._groupTotals[col.id] : null;
                        return cellValue;
                    };
                }
                
                if (col.id == actualRow._groupId) {
                    var group = this._tableControl.groupManager.getGroupById(actualRow._groupId);
                    if (group) {
                        let cellContext = {
                            reportControl: this._tableControl.reportControl,
                            row: actualRow,
                            level: rowModel.level,
                            modelEnvironment: this._tableControl.modelEnvironment,
                            group: group,
                            defaultFormatter: Solovis.Reports.Formatter.formatValueAsHtml
                        };

                        var groupRep = actualRow._groupRep;
                        var groupHeaderHtml = this._tableControl._formatCellHtml(groupRep, cellContext);
                        if (groupHeaderHtml === null || groupHeaderHtml === "")
                            groupHeaderHtml = "[Unknown]";

                        let cellText = $("<div>").html(groupHeaderHtml).text();
                        return cellText;
                    }
                    else {
                        return null;
                    }
                }
                else {
                    var cellValue = cellValueGetter(rowModel.row, col);
                    return cellValue;
                }                
            }
            else {
                let actualRow = rowModel.row;

                var cellValue = this._tableControl._getColumnValueForRow(col, actualRow);
                return cellValue;
            }
        }
    }
}